import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic, MicOff, Paperclip, Settings, LogOut, Send, X } from "lucide-react";
import LivingOrb from "@/components/me/LivingOrb";
import PhysicalPresenceScore from "@/components/me/PhysicalPresenceScore";
import WorkStreamOrb from "@/components/me/WorkStreamOrb";
import { chipToAction } from "@/lib/actionBus";
import { api, formatApiErrorDetail } from "@/lib/api";
import { installCrashReporter, reportPilotEvent } from "@/lib/pilotLog";
import { createOrganClient } from "@/lib/organClient";
import { createVoiceClient, isVoiceSupported } from "@/lib/voiceClient";
import { useLivingConversation } from "@/lib/useLivingConversation";
import { useSmartAutoScroll } from "@/lib/useSmartAutoScroll";
import { AUDIO_INTENT, classifyAudioIntent, isAcceptanceReportCommand, isCastCommand, isCommandLike, isPilotStatusCommand, parseYoutubeCommand } from "@/lib/audioIntent";
import { playSpokenAudio, cancelAllSpokenAudio, setMicGate } from "@/lib/audioChain";
import { createWhisperMic } from "@/lib/whisperMic";
import {
  useFounderGreeting,
} from "@/components/voice/FounderPresence";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

/**
 * ME's first living experience.
 *
 * Single dominant element: the Living Orb.
 * No sidebar. No dashboard. No login screen dominating.
 *
 * Lifecycle
 *   - On mount: GET /api/brain/wake (sub-300ms) → personalised greeting
 *     appears as a single calm line above the orb.
 *   - SpeechRecognition (if available) listens passively for the
 *     wake-word "ME" or "Me". When detected, listening state activates
 *     and full utterance is streamed into the conversation.
 *   - Conversation is borderless — same orb, no popup. Tokens stream
 *     onto the screen below the orb; the orb's state mirrors the LLM
 *     lifecycle (listening → thinking → speaking → idle).
 *   - A subtle floating "Open dashboard" pill exists for power users,
 *     but does not dominate.
 */
export default function LivingPresencePage() {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [state, setState] = useState("idle"); // idle|listening|thinking|speaking|alert|preparing|waiting
  const [greeting, setGreeting] = useState("");
  const [invitations, setInvitations] = useState([]); // PreparedInsight pills
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [messages, setMessages] = useState([]); // {role, content, streaming?}
  const [textInput, setTextInput] = useState("");
  const [error, setError] = useState("");
  const [goals, setGoals] = useState([]); // CTO #91 — open goals tray
  const [mind, setMind] = useState(null); // CTO #93 — last cognition summary
  const [actions, setActions] = useState([]); // CTO #94 — Mind-emitted action chips
  const [unfinished, setUnfinished] = useState([]); // CTO #99 — paused ActionRuns
  // CTO Directive #126.2 — remember the last assistant reply so a
  // "Tekrar et" / "Say that again" utterance can re-speak it, and a
  // "Duymuyorum" can trigger the same replay before falling back to
  // written-only mode.
  const lastAssistantReplyRef = useRef("");
  // CTO Directive #126.3 §P0-A — honest written-continue state. True
  // only when TTS produced no audio OR playback could not be
  // verified. Cleared the moment a later playback verifies.
  const [writtenContinue, setWrittenContinue] = useState(false);
  const postTurnSignal = useCallback(async (phase, extras = {}) => {
    try { await api.post("/me/voice/turn-signal", { phase, ...extras }); } catch { /* silent */ }
  }, []);
  const enterWrittenContinue = useCallback(() => {
    setWrittenContinue(true);
    postTurnSignal("written_continuation");
  }, [postTurnSignal]);
  // CTO #127 §P0-F — the UI openly shows when the cast target is a
  // simulation; ME never claims "yayın başladı" for it.
  const [castSimulated, setCastSimulated] = useState(false);
  // CTO Directive #126.2 — natural voice being. The founder
  // greeting still fires (via the existing audio chain), but there
  // is NO clickable "Duyuyorum / Duymuyorum" modal any more:
  // ME understands the physical audio state from the natural
  // conversation via /voice/classify-utterance.
  useFounderGreeting({ patientContextActive: false });
  // CTO #103 — Living Presence (voice) state. Separate from the old
  // press-and-talk recognition above. ``livingVoiceOn`` is set when
  // the user explicitly grants the mic; ``livingVoiceState`` mirrors
  // the orb so it can pulse listening/waking/speaking.
  const [livingVoiceSupported] = useState(() => isVoiceSupported());
  const [livingVoiceOn, setLivingVoiceOn] = useState(false);
  const [livingVoiceState, setLivingVoiceState] = useState("idle");
  const livingVoiceRef = useRef(null);

  const recogRef = useRef(null);
  const micRef = useRef(null);
  const submitRef = useRef(null);
  const audioRef = useRef({ ctx: null, analyser: null, raf: 0, stream: null });
  const sessionRef = useRef(`live-${Math.random().toString(36).slice(2, 10)}`);
  const typingRef = useRef(null);

  // --- 1. Wake greeting + ME's prepared work ----------------------------
  useEffect(() => { installCrashReporter(); }, []);
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    api.get(`/brain/wake?language=${language}`).then((r) => {
      if (cancelled) return;
      setGreeting(r.data?.greeting || "");
      setInvitations(Array.isArray(r.data?.invitations) ? r.data.invitations.slice(0, 3) : []);
      // ME's mind tells the orb what motion to take when idle.
      const srv = r.data?.orb_state;
      if (srv && ["idle", "preparing", "waiting"].includes(srv)) {
        setState((cur) => (cur === "idle" ? srv : cur));
      }
    }).catch(() => setGreeting(""));

    // Subtle invitation refresh: every 30s ME re-checks its tray so
    // new prepared work emerges silently into view.
    const t = setInterval(async () => {
      try {
        const ins = await api.get("/living/insights?status=pending&limit=3");
        if (cancelled) return;
        const arr = Array.isArray(ins.data) ? ins.data : [];
        setInvitations(arr.map((i) => ({
          id: i.id, kind: i.kind,
          summary_tr: i.summary_tr, summary_en: i.summary_en,
          payload: i.payload || {},
          person_id: i.payload?.person_id,
          candidate_person_ids: i.payload?.candidate_person_ids || [],
        })));
      } catch { /* silent */ }
    }, 30000);

    return () => { cancelled = true; clearInterval(t); };
  }, [user, language]);

  // CTO #91 — show the user the vision ME is silently tracking. We
  // only render goals here; we never render a "create" button. New
  // goals are detected from chat utterances.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const fetchGoals = async () => {
      try {
        const r = await api.get("/graph/goals?limit=6");
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setGoals(list.filter((g) => (g.props || {}).status !== "done").slice(0, 4));
      } catch { /* silent */ }
    };
    fetchGoals();
    const id = setInterval(fetchGoals, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // CTO #93 — silently surface the latest cognition. We never block
  // the UI on the Mind; if Mind is slow, the badge simply stays empty.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const fetchMind = async () => {
      try {
        const r = await api.get("/mind/cognitions?limit=1");
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        setMind(arr[0] || null);
      } catch { /* silent */ }
    };
    fetchMind();
    const id = setInterval(fetchMind, 35000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // CTO #99 — at wake, ask the bus what ME was doing before we lost
  // power / the user navigated away / the conversation paused. Surface
  // these as a short "Continue?" chip row so ME *resumes* rather than
  // restarts.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const fetchUnfinished = async () => {
      try {
        const r = await api.get("/mind/actions/runs/unfinished");
        if (cancelled) return;
        setUnfinished(Array.isArray(r.data) ? r.data.slice(0, 3) : []);
      } catch { /* silent */ }
    };
    fetchUnfinished();
    const id = setInterval(fetchUnfinished, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // CTO Directive #101 — silent sensory organ presence. Attach this
  // browser as one of ME's organs, heartbeat every 30s, detach on
  // page unload. ME never moves; only its senses change.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const client = createOrganClient();
    client.start();
    return () => client.stop();
  }, [user]);

  // CTO Directive #101 — Living Consciousness pickup. When the page
  // becomes visible again after > 5 min, ask ME for the last few
  // thoughts so the orb can softly continue "where we were" instead
  // of starting over.
  const [continuation, setContinuation] = useState([]);
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const lsKey = `me-last-seen-${user.id}`;
    const onPickup = async () => {
      try {
        const last = localStorage.getItem(lsKey);
        const now = new Date().toISOString();
        // Always update last_seen so the next pickup measures from now.
        const stale = !last || (Date.now() - new Date(last).getTime()) > 5 * 60 * 1000;
        localStorage.setItem(lsKey, now);
        if (!stale) return;
        const r = await api.get("/me/stream", { params: { limit: 3 } });
        const arr = Array.isArray(r.data) ? r.data : [];
        // Only surface entries with an intention — the orb cares about
        // what ME was saying, not raw bookkeeping.
        const said = arr.filter((x) => x.intention_tr || x.intention_en)
                       .slice(0, 2);
        setContinuation(said);
      } catch { /* silent */ }
    };
    onPickup();
    const onVis = () => { if (document.visibilityState === "visible") onPickup(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [user]);

  // CTO Directive #102.1 — silently track the attention field so
  // the orb can hint where ME's attention is right now. Subtle —
  // just three thin bars under the mind-state footer; never noisy.
  const [attention, setAttention] = useState([]);
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/me/attention");
        if (cancelled) return;
        const field = Array.isArray(r.data?.attention_field)
          ? r.data.attention_field.slice(0, 3) : [];
        setAttention(field);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // CTO Directive #103 — Living Presence voice loop. The client is
  // silent: no UI here. Toggling on starts continuous listening for
  // the wake word ("Merhaba ME"); the orb mirrors the lifecycle via
  // ``setState`` so listening→waking→speaking→idle is visible without
  // any extra widget.
  useEffect(() => {
    if (!livingVoiceOn || !livingVoiceSupported || !user) return;
    const client = createVoiceClient({
      language: language === "tr" ? "tr-TR" : "en-US",
      onStateChange: (s) => {
        setLivingVoiceState(s);
        const orbMap = {
          idle: "idle", listening: "listening", waking: "preparing",
          thinking: "preparing", speaking: "speaking",
        };
        setState(orbMap[s] || "idle");
      },
      onUserText: (t) => {
        if (!t) return;
        setMessages((m) => [...m, { role: "user", content: t }]);
      },
      onAssistantText: (t) => {
        if (!t) return;
        setMessages((m) => {
          const last = m[m.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            return [...m.slice(0, -1),
                    { role: "assistant", content: `${last.content} ${t}`.trim(), streaming: true }];
          }
          return [...m, { role: "assistant", content: t, streaming: true }];
        });
      },
    });
    livingVoiceRef.current = client;
    client.start();
    return () => { try { client.stop(); } catch { /* silent */ } };
  }, [livingVoiceOn, livingVoiceSupported, user, language]);

  const dismissInvitation = useCallback(async (id) => {
    setInvitations((v) => v.filter((i) => i.id !== id));
    try { await api.post(`/living/insights/${id}/ack`); } catch { /* silent */ }
  }, []);

  // CTO Directive #90 — natural confirmation actions for discovery
  // insights. No forms, no navigation. Each chip is one sentence that
  // resolves the observation inside the same Living Orb context.
  const DISCOVERY_KINDS = useMemo(() => new Set([
    "person_candidate", "returning_person", "familiar_voice",
    "familiar_device", "possible_duplicate", "relationship_update",
  ]), []);

  const actOnDiscovery = useCallback(async (invitation, action) => {
    setInvitations((v) => v.filter((i) => i.id !== invitation.id));
    try {
      const path = `/living/insights/${invitation.id}/${action}`;
      const body = action === "merge"
        ? { primary_person_id: invitation.candidate_person_ids?.[0] }
        : {};
      await api.post(path, body);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  // --- 2. Audio meter (used for orb bloom) -------------------------------
  const startAudioMeter = useCallback(async () => {
    if (audioRef.current.ctx) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        const avg = sum / buf.length / 255; // 0..1
        setAudioLevel(avg);
        audioRef.current.raf = requestAnimationFrame(tick);
      };
      audioRef.current = { ctx, analyser, raf: requestAnimationFrame(tick), stream };
    } catch {
      // Mic denied — silently fall back to no meter; orb still breathes.
    }
  }, []);

  const stopAudioMeter = useCallback(() => {
    const a = audioRef.current;
    if (a.raf) cancelAnimationFrame(a.raf);
    if (a.stream) a.stream.getTracks().forEach((tr) => tr.stop());
    if (a.ctx) try { a.ctx.close(); } catch { /* noop */ }
    audioRef.current = { ctx: null, analyser: null, raf: 0, stream: null };
    setAudioLevel(0);
  }, []);

  // --- 3. Voice — real hearing via backend Whisper -----------------------
  // Continuous conversation loop: mic stays open, a level-based VAD
  // cuts speech segments, each segment goes to /api/me/voice/listen
  // (Whisper). While ME speaks the mic is gated (audioChain micGate)
  // so it never hears its own voice. No browser SpeechRecognition.
  useEffect(() => {
    const supported = Boolean(
      navigator.mediaDevices?.getUserMedia && window.MediaRecorder,
    );
    setVoiceSupported(supported);
    if (!supported) return;
    const mic = createWhisperMic({
      language,
      onLevel: (lvl) => setAudioLevel(lvl),
      onState: (st) => {
        if (st === "transcribing") setState("thinking");
      },
      onTranscript: (text) => {
        setTranscript(text);
        const cleaned = text.trim();
        const stripped = cleaned.replace(/^me[\s,.!?-]+/i, "").trim();
        const utterance = stripped || cleaned;
        if (utterance.length > 1) {
          // CTO #126.3 §P0-C — transcript physically arrived.
          try { api.post("/me/voice/turn-signal", { phase: "transcript_received", utterance_chars: utterance.length }); } catch { /* silent */ }
          submitRef.current && submitRef.current(utterance);
        }
      },
      onError: () => {
        reportPilotEvent("microphone_failure", { note: "whisper mic error" });
      },
    });
    micRef.current = mic;
    setMicGate((muted) => mic.setMuted(muted));
    return () => {
      mic.stop();
      micRef.current = null;
      setMicGate(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const toggleVoice = useCallback(async () => {
    const mic = micRef.current;
    if (!mic) return;
    if (listening) {
      mic.stop();
      setListening(false);
      setState("idle");
      return;
    }
    setError("");
    setTranscript("");
    try {
      await mic.start();
      setListening(true);
      setState("listening");
      // CTO #126.3 §P0-C — honest chain link: the microphone is
      // physically capturing from this moment on.
      postTurnSignal("mic_captured");
    } catch {
      setVoiceSupported(false);
      reportPilotEvent("microphone_failure", { note: "mic permission denied" });
    }
  }, [listening, postTurnSignal]);

  // --- 4. Conversation (streaming feel) ----------------------------------
  function streamAssistant(text) {
    lastAssistantReplyRef.current = text;
    return new Promise((resolve) => {
      let i = 0;
      const tokens = text.split(/(\s+)/); // keep whitespace tokens
      setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
      setState("speaking");
      if (typingRef.current) clearInterval(typingRef.current);
      typingRef.current = setInterval(() => {
        i++;
        const sliced = tokens.slice(0, i).join("");
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") {
            copy[copy.length - 1] = { ...last, content: sliced };
          }
          return copy;
        });
        if (i >= tokens.length) {
          clearInterval(typingRef.current);
          typingRef.current = null;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, streaming: false };
            }
            return copy;
          });
          setState("idle");
          resolve();
        }
      }, 14);
    });
  }

  // CTO Directive #126.2 — speak the given text through the real
  // audio chain and return whether the browser reported actual
  // playback ("verified"). If unverified we fall back to a written
  // line and DO NOT claim ME spoke. Never blocks the UI.
  const speakReply = useCallback(async (text) => {
    if (!text) return { verified: false };
    try {
      const r = await api.post("/me/voice/speak", { text, language });
      const payload = r.data?.payload || {};
      if (!payload.audio_base64) return { verified: false };
      const handle = playSpokenAudio({
        audioBase64: payload.audio_base64,
        mimeType: payload.mime_type || "audio/mpeg",
        utteranceId: "reply-" + Date.now(),
      });
      const state = await handle.done;
      const verified = Boolean(state?.verified);
      if (verified) setWrittenContinue(false);
      else reportPilotEvent("voice_failure", { note: "playback unverified" });
      return { verified };
    } catch {
      reportPilotEvent("voice_failure", { note: "speak request failed" });
      return { verified: false };
    }
  }, [language]);

  // CTO Directive #126.2 — natural audio-state intent handler.
  // No modal, no button click required — the utterance itself IS
  // the command. Unknown intent falls back to the LLM path.
  const handleAudioIntent = useCallback(async (intent, utterance) => {
    // CTO #129 §P0.2 — living greeting: "Merhaba Mi" →
    // "Merhaba Dr. Mehmet Akdemir. Buradayım." (personalized by the
    // backend from the signed-in identity, spoken aloud).
    if (intent === AUDIO_INTENT.GREETING) {
      let line = "Merhaba. Buradayım.";
      try {
        const r = await api.post("/me/voice/classify-utterance", {
          text: utterance,
        });
        if (r.data?.suggested_reply_tr) line = r.data.suggested_reply_tr;
      } catch { /* offline — generic greeting still works */ }
      await streamAssistant(line);
      speakReply(line); // fire-and-forget
      return;
    }
    if (intent === AUDIO_INTENT.STOP_REQUESTED) {
      cancelAllSpokenAudio();
      setState("idle");
      const line = "Durdum. Sen söyleyene kadar bekliyorum.";
      await streamAssistant(line);
      return;
    }
    if (intent === AUDIO_INTENT.ACKNOWLEDGED) {
      const line = "Güzel, devam ediyorum.";
      await streamAssistant(line);
      speakReply(line);  // fire-and-forget
      return;
    }
    if (intent === AUDIO_INTENT.REPEAT_REQUESTED
        || intent === AUDIO_INTENT.NOT_HEARD) {
      const last = lastAssistantReplyRef.current;
      if (!last) {
        const line = "Henüz söyleyecek bir cümlem yok; seni dinliyorum.";
        await streamAssistant(line);
        return;
      }
      // Try to re-speak.
      cancelAllSpokenAudio();
      const attempt = await speakReply(last);
      if (attempt.verified) return;
      // Honest fallback — text-only continuation.
      const fallback = (
        "Yanıtımı hazırladım; ses çıkışını doğrulayamadım. "
        + "Yazılı olarak devam ediyorum."
      );
      await streamAssistant(fallback);
      enterWrittenContinue();
      setState("idle");
      return;
    }
    if (intent === AUDIO_INTENT.OUTPUT_HELP_REQUESTED) {
      const line = (
        "Ses çıkışına birlikte bakalım — Windows'ta hoparlör seçimi, "
        + "sekmenin sessize alınmadığı ve izinler kontrol edilebilir."
      );
      await streamAssistant(line);
      speakReply(line);
      return;
    }
    // Should not reach here — UNKNOWN is filtered by caller.
    setState("idle");
  }, [speakReply, enterWrittenContinue]);

  // CTO Directive #126.3 §P0-B — ambiguous audio-command. ME asks
  // "Bunu nasıl yapmamı istersin?" in writing AND out loud through
  // the real TTS chain — once, never looped. If playback cannot be
  // verified the Orb honestly enters the written-continue state.
  const askHowToProceed = useCallback(async (text) => {
    const line = "Bunu nasıl yapmamı istersin?";
    reportPilotEvent("failed_command", {
      note: "ambiguous voice command",
      metadata: { utterance_chars: (text || "").length },
    });
    await streamAssistant(line);
    try {
      const r = await api.post("/me/voice/classify-utterance", {
        text, speak_hint: true,
      });
      const d = r.data || {};
      if (d.hint_audio_base64) {
        const handle = playSpokenAudio({
          audioBase64: d.hint_audio_base64,
          mimeType: d.hint_mime_type || "audio/mpeg",
          utteranceId: "hint-" + Date.now(),
        });
        const st = await handle.done;
        if (st?.verified) setWrittenContinue(false);
        else enterWrittenContinue();
      } else {
        enterWrittenContinue();
      }
    } catch {
      enterWrittenContinue();
    }
    setState("idle");
  }, [enterWrittenContinue]);

  // CTO #127 §P0-E — natural cast command flow. The backend parses,
  // gates through mesh trust + clinical approval, and answers with
  // one honest Turkish sentence (optionally spoken).
  const handleCastCommand = useCallback(async (text) => {
    setState("preparing");
    try {
      const r = await api.post("/me/cast/intent", {
        utterance: text, speak_hint: true,
      });
      const d = r.data || {};
      if (typeof d.simulated === "boolean") setCastSimulated(d.simulated);
      if (d.message_tr) await streamAssistant(d.message_tr);
      if (Array.isArray(d.options) && d.options.length > 0) {
        const list = d.options.map((o) => o.label_tr).join(" · ");
        setMessages((m) => [...m, { role: "assistant", content: list }]);
      }
      if (d.hint_audio_base64) {
        const handle = playSpokenAudio({
          audioBase64: d.hint_audio_base64,
          mimeType: d.hint_mime_type || "audio/mpeg",
          utteranceId: "cast-hint-" + Date.now(),
        });
        const st = await handle.done;
        if (st?.verified) setWrittenContinue(false);
        else enterWrittenContinue();
      }
    } catch (e) {
      if (!e.response) {
        reportPilotEvent("cast_failure", { note: "cast intent unreachable" });
        await streamAssistant(
          "Sunucuya bağlanamadım. Yazılı olarak devam ediyorum."
        );
        enterWrittenContinue();
      } else {
        reportPilotEvent("cast_failure", { note: "cast intent error" });
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
    setState("idle");
  }, [enterWrittenContinue]);

  const submit = useCallback(async (utterance) => {
    const text = (utterance || textInput).trim();
    if (!text) return;
    setError("");
    setTextInput("");
    setTranscript("");

    // CTO Directive #126.2 §P0-C — before the LLM path, check for a
    // pure audio-state intent. Deterministic client-side match; the
    // backend classifier is the canonical source of truth (tested).
    const audioIntent = classifyAudioIntent(text);
    if (audioIntent !== AUDIO_INTENT.UNKNOWN) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      await handleAudioIntent(audioIntent, text);
      return;
    }
    // "YouTube'da X aç" — ME opens the media the user asked for.
    const yt = parseYoutubeCommand(text);
    if (yt) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      const url = yt.query
        ? `https://www.youtube.com/results?search_query=${encodeURIComponent(yt.query)}`
        : "https://www.youtube.com";
      try { window.open(url, "_blank", "noopener"); } catch { /* silent */ }
      const line = yt.query
        ? `YouTube'da "${yt.query}" için aramayı açtım.`
        : "YouTube'u açtım.";
      await streamAssistant(line);
      speakReply(line);
      setState("idle");
      return;
    }
    // CTO #130.3 — "Fiziksel kabul raporunu indir." Deterministic;
    // downloads the official acceptance PDF of the last closed session.
    if (isAcceptanceReportCommand(text)) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      setState("preparing");
      try {
        const r = await api.post("/me/pilot/intent", {
          utterance: text, speak_hint: false,
        });
        const d = r.data || {};
        if (d.kind === "acceptance_report" && d.message_tr) {
          await streamAssistant(d.message_tr);
          if (d.report_url && d.session_id) {
            const pdf = await api.get(
              `/me/pilot/acceptance/${d.session_id}/report.pdf`,
              { responseType: "blob" },
            );
            const url = URL.createObjectURL(pdf.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `meos-fiziksel-kabul-${d.session_id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }
        }
      } catch (e) {
        if (!e.response) {
          await streamAssistant(
            "Sunucuya bağlanamadım. Yazılı olarak devam ediyorum."
          );
          enterWrittenContinue();
        }
      }
      setState("idle");
      return;
    }
    // CTO #129.1 — spoken pilot health summary: one honest sentence
    // from recorded events only; spoken when TTS delivers.
    if (isPilotStatusCommand(text)) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      setState("preparing");
      try {
        const r = await api.post("/me/pilot/intent", {
          utterance: text, speak_hint: true,
        });
        const d = r.data || {};
        if (d.recognised && d.message_tr) {
          await streamAssistant(d.message_tr);
          if (d.hint_audio_base64) {
            const handle = playSpokenAudio({
              audioBase64: d.hint_audio_base64,
              mimeType: d.hint_mime_type || "audio/mpeg",
              utteranceId: "pilot-" + Date.now(),
            });
            const st = await handle.done;
            if (st?.verified) setWrittenContinue(false);
            else enterWrittenContinue();
          }
        }
      } catch (e) {
        if (!e.response) {
          await streamAssistant(
            "Sunucuya bağlanamadım. Yazılı olarak devam ediyorum."
          );
          enterWrittenContinue();
        }
      }
      setState("idle");
      return;
    }
    // CTO #127 §P0-E — cast commands go to the cast organ, not the
    // LLM. Checked before command-likeness ("sunumu televizyona aç"
    // must not be treated as an ambiguous audio command).
    if (isCastCommand(text)) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      await handleCastCommand(text);
      return;
    }
    // CTO #126.3 §P0-B — audio-command-shaped but ambiguous: ask how
    // to proceed (written + spoken once) instead of guessing.
    if (isCommandLike(text)) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      await askHowToProceed(text);
      return;
    }

    setMessages((m) => [...m, { role: "user", content: text }]);
    setState("thinking");

    // Companion runtime — workspace prep is fire-and-forget so it
    // NEVER delays ME's spoken reply (was a blocking await before).
    api.post("/brain/workspace", { utterance: text, language }).then((wsRes) => {
      const ws = wsRes.data || {};
      const top = ws.intent;
      const actions = ws.suggested_actions || [];
      if (top || actions.length > 0) {
        setMessages((m) => [
          ...m,
          {
            role: "workspace",
            content: "",
            workspace: {
              intent: top,
              actions,
              evidence_count: Object.values(ws.knowledge_buckets || {}).flat().length,
              warm_count: (ws.warm_cache || []).length,
            },
          },
        ]);
      }
    }).catch(() => {
      // Workspace prep is best-effort — never blocks ME's reply.
    });

    // CTO Directive #94 — Mind decides what action(s) belong with
    // this utterance and emits chips. Fire in parallel with the chat
    // reply so the user sees ME's hands reach for the right tool
    // immediately, not after the spoken reply finishes.
    const mindPromise = api.post("/mind/think", { utterance: text, language })
      .then((mindRes) => {
        const arr = Array.isArray(mindRes.data?.actions) ? mindRes.data.actions : [];
        setActions(arr.slice(0, 2));
        setMind(mindRes.data);
      })
      .catch(() => { /* Mind is additive — silent failure is acceptable. */ });

    try {
      const t0 = performance.now();
      const { data } = await api.post("/brain/chat", {
        message: text,
        session_id: sessionRef.current,
        task_kind: "general",
        language,
      });
      // CTO #129 §P0.4 — pilot latency sample for every real turn.
      reportPilotEvent("latency", {
        latency_ms: Math.round(performance.now() - t0),
        note: "brain/chat turn",
      });
      const reply = data?.message?.content || data?.content || data?.reply || "…";
      // Adopt the server-side session_id so all later turns continue
      // the same persistent conversation — across pages and devices.
      if (data?.session_id) sessionRef.current = data.session_id;
      // ME SPEAKS every reply — voice and text start together. The
      // mic gate silences the microphone while the audio plays.
      const speakPromise = speakReply(reply);
      await streamAssistant(reply);
      await speakPromise;
    } catch (e) {
      if (!e.response) {
        // CTO #129 §P0.1 — production backend unreachable. Honest,
        // crash-free continuation; no technical jargon.
        reportPilotEvent("backend_timeout", { note: "brain/chat unreachable" });
        await streamAssistant(
          "Sunucuya bağlanamadım. Yazılı olarak devam ediyorum."
        );
        enterWrittenContinue();
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
      setState("alert");
      setTimeout(() => setState("idle"), 2000);
    }

    await mindPromise;
  }, [textInput, language, speakReply]);

  // Keep the mic's transcript handler pointing at the latest submit.
  useEffect(() => { submitRef.current = submit; }, [submit]);

  // --- Document upload: ME reads what the user hands it -------------------
  const fileInputRef = useRef(null);
  const onFileChosen = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setMessages((m) => [...m, { role: "user", content: `📎 ${file.name}` }]);
    setState("thinking");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("language", language);
      const r = await api.post("/brain/analyze-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const d = r.data || {};
      const line = d.disclaimer_tr
        ? `${d.summary_tr}\n\n${d.disclaimer_tr}`
        : (d.summary_tr || "Belgeyi okuyamadım.");
      const speakP = d.ok ? speakReply(d.summary_tr) : Promise.resolve();
      await streamAssistant(line);
      await speakP;
    } catch (err) {
      await streamAssistant(
        "Belgeyi alamadım ya da yorumlayamadım. Tekrar deneyebilir miyiz?"
      );
    }
    setState("idle");
  }, [language, speakReply]);

  // CTO Directive #99 — the chip is no longer a label. It is a verb.
  // Clicking it dispatches one Action through the bus and pulls ME's
  // narrated outcome back into the conversation. The orb's state
  // tracks the run lifecycle (preparing → executing → completed).
  const executeAction = useCallback(async (chip) => {
    const body = chipToAction(chip);
    if (!body) {
      setActions((a) => a.filter((x) => x.kind !== chip.kind));
      return;
    }
    setActions((a) => a.filter((x) => x.kind !== chip.kind));
    setState("preparing");
    try {
      const r = await api.post("/mind/act", { ...body, language, session_id: sessionRef.current });
      const data = r.data || {};
      const intention = language === "tr" ? data.intention_tr : data.intention_en;
      const orb = data.orb_state || "idle";
      // Mirror the bus' lifecycle onto the orb.
      const orbToState = {
        preparing: "preparing",
        executing: "speaking",
        waiting:   "waiting",
        completed: "idle",
        observing: "observing",
        alert:     "alert",
        idle:      "idle",
      };
      setState(orbToState[orb] || "idle");
      if (intention) {
        await streamAssistant(intention);
      }
      // If the run paused (e.g. capsule pairing required), surface it
      // alongside other unfinished runs so the user can resume later.
      if (data.status === "paused") {
        setUnfinished((u) => [data, ...u].slice(0, 3));
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setState("alert");
      setTimeout(() => setState("idle"), 2000);
    }
  }, [language]);

  const resumeRun = useCallback(async (run) => {
    setUnfinished((u) => u.filter((x) => x.id !== run.id));
    setState("preparing");
    try {
      const r = await api.post(`/mind/actions/runs/${run.id}/resume`);
      const data = r.data || {};
      const intention = language === "tr" ? data.intention_tr : data.intention_en;
      if (intention) await streamAssistant(intention);
      setState("idle");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setState("alert");
      setTimeout(() => setState("idle"), 2000);
    }
  }, [language]);

  useEffect(() => {
    return () => {
      stopAudioMeter();
      if (typingRef.current) clearInterval(typingRef.current);
    };
  }, [stopAudioMeter]);

  // --- render ------------------------------------------------------------
  // CTO Directive #111 — Living Conversation Stream is the *only*
  // source of truth for the orb. We keep polling it whenever the
  // user is on the page; local state is a fallback cache while the
  // stream hydrates.
  const {
    state: livingState,
    profile: livingProfile,
  } = useLivingConversation({ enabled: Boolean(user), intervalMs: 900 });

  // Fold the server state into the palette taxonomy the orb knows.
  const livingOrbState = React.useMemo(() => {
    const map = {
      listening: "listening",
      thinking: "preparing",
      waiting: "waiting",
      speaking: "speaking",
    };
    return map[livingState] || state;
  }, [livingState, state]);

  const orbState = state;
  // CTO #126.3 §P0-A — written-continue overrides only calm states;
  // an actively speaking / listening orb keeps its real state.
  const orbRenderState = (
    writtenContinue
    && !["speaking", "listening", "preparing"].includes(livingOrbState)
  ) ? "written" : livingOrbState;

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-slate-950 px-6 py-10 text-slate-100"
      data-testid="living-presence-page"
      style={{
        backgroundImage:
          "radial-gradient(circle at 50% 28%, rgba(120,113,108,0.22), transparent 60%), radial-gradient(circle at 50% 80%, rgba(15,23,42,0.95), #020617 70%)",
      }}
    >
      {/* Soft top chrome — barely there */}
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between px-6 py-4 text-xs text-slate-400">
        <div className="mono uppercase tracking-[0.18em]" data-testid="me-presence-banner">ME · {language === "tr" ? "Buradayım" : "I am here"}</div>
        <div className="flex items-center gap-2">
          {livingVoiceSupported && (
            <button
              type="button"
              onClick={() => setLivingVoiceOn((v) => !v)}
              data-testid="living-voice-toggle"
              className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] backdrop-blur-md transition-colors ${
                livingVoiceOn
                  ? "border-sky-400/70 bg-sky-400/10 text-sky-100 hover:border-sky-300"
                  : "border-slate-700/80 text-slate-300 hover:border-slate-500 hover:text-white"
              }`}
              title={language === "tr"
                ? (livingVoiceOn
                    ? `Dinliyorum · ${livingVoiceState}`
                    : "Sesli konuşalım — 'Merhaba ME' de")
                : (livingVoiceOn
                    ? `Listening · ${livingVoiceState}`
                    : "Let's talk — say 'Hey ME'")}
            >
              <Mic className="-mt-0.5 mr-1.5 inline h-3 w-3" />
              {language === "tr"
                ? (livingVoiceOn ? "Dinliyor" : "Sesli")
                : (livingVoiceOn ? "Listening" : "Voice")}
            </button>
          )}
          <Link
            to="/dashboard"
            data-testid="open-dashboard-link"
            className="rounded-full border border-slate-700/80 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-slate-300 backdrop-blur-md transition-colors hover:border-slate-500 hover:text-white"
          >
            <Settings className="-mt-0.5 mr-1.5 inline h-3 w-3" />
            {t.living?.openDashboard || (language === "tr" ? "Çalışma alanı" : "Workspace")}
          </Link>
          <button
            type="button"
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="living-logout-btn"
            className="rounded-full border border-slate-700/80 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-slate-300 backdrop-blur-md transition-colors hover:border-rose-400/60 hover:text-rose-200"
          >
            <LogOut className="-mt-0.5 mr-1.5 inline h-3 w-3" />
            {t.nav?.logout || "Çıkış"}
          </button>
        </div>
      </div>

      {/* The orb — CTO Directive #111. The Living Orb is driven by
          the server's Voice Stream when available (`livingState`
          from `useLivingConversation`); the legacy `orbState` acts
          as an intermediate cache while the stream is still
          hydrating so the orb is never blank. */}
      <div className="flex flex-col items-center" data-testid="me-living-form">
        <LivingOrb
          state={orbRenderState}
          level={audioLevel}
          size={420}
          profile={livingProfile}
        />
        {greeting && (
          <p
            className="mt-2 text-center text-lg font-light tracking-[0.01em] text-slate-200 sm:text-xl"
            data-testid="wake-greeting"
          >
            {greeting}
          </p>
        )}
        {invitations.length > 0 && (
          <div
            className="mt-3 flex max-w-2xl flex-col items-center gap-2"
            data-testid="me-invitations"
          >
            {invitations.map((inv) => {
              const isDiscovery = DISCOVERY_KINDS.has(inv.kind);
              const label = language === "tr" ? inv.summary_tr : inv.summary_en;
              if (!isDiscovery) {
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => dismissInvitation(inv.id)}
                    data-testid={`me-invitation-${inv.id}`}
                    title={language === "tr" ? "Onayla / kapat" : "Acknowledge"}
                    className="group rounded-full border border-amber-200/20 bg-amber-50/[0.04] px-4 py-1.5 text-xs text-amber-100/90 backdrop-blur-md transition-colors hover:border-amber-200/50 hover:text-amber-50"
                    style={{ animation: "me-invitation-emerge 600ms ease-out both" }}
                  >
                    <span className="mono mr-2 text-[9px] uppercase tracking-[0.18em] text-amber-200/70">
                      {inv.kind.replace(/_/g, " ")}
                    </span>
                    {label}
                  </button>
                );
              }
              // Discovery insight — conversational chips, no form, no modal.
              const remember = language === "tr" ? "Hatırla" : "Remember";
              const notNow = language === "tr" ? "Şimdi değil" : "Not now";
              const merge = language === "tr" ? "Birleştir" : "Merge";
              const showMerge = inv.kind === "possible_duplicate"
                && (inv.candidate_person_ids || []).length > 0;
              return (
                <div
                  key={inv.id}
                  data-testid={`me-discovery-${inv.id}`}
                  className="flex flex-col items-center gap-2 rounded-2xl border border-amber-200/25 bg-amber-50/[0.05] px-4 py-3 text-center backdrop-blur-md"
                  style={{ animation: "me-invitation-emerge 600ms ease-out both" }}
                >
                  <div className="mono text-[9px] uppercase tracking-[0.18em] text-amber-200/70">
                    {inv.kind.replace(/_/g, " ")}
                  </div>
                  <div className="text-sm text-amber-50/95">{label}</div>
                  <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => actOnDiscovery(inv, "confirm")}
                      data-testid={`discovery-confirm-${inv.id}`}
                      className="rounded-full border border-amber-200/60 bg-amber-200/20 px-3 py-1 text-xs text-amber-50 transition-colors hover:bg-amber-200/30"
                    >
                      {remember}
                    </button>
                    {showMerge && (
                      <button
                        type="button"
                        onClick={() => actOnDiscovery(inv, "merge")}
                        data-testid={`discovery-merge-${inv.id}`}
                        className="rounded-full border border-slate-500/60 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-slate-300"
                      >
                        {merge}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => actOnDiscovery(inv, "dismiss")}
                      data-testid={`discovery-dismiss-${inv.id}`}
                      className="rounded-full border border-slate-700/70 px-3 py-1 text-xs text-slate-400 transition-colors hover:text-slate-200"
                    >
                      {notNow}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-2 text-center text-[11px] uppercase tracking-[0.24em] text-slate-500" data-testid="me-orb-status-line">
          {/* CTO Directive #121.4 — no fake presence claims here.
              The line only says what the *stream* verified. */}
          {orbState === "listening" && (language === "tr" ? "seni duyuyorum" : "hearing you")}
          {orbState === "speaking" && (language === "tr" ? "sesim çalıyor" : "speaking aloud")}
          {orbState === "preparing" && (language === "tr" ? "hazırlıyorum" : "preparing")}
          {orbState === "alert" && (language === "tr" ? "kontrol" : "check")}
          {(orbState === "idle" || orbState === "waiting" || orbState === "thinking") &&
            (language === "tr" ? "buradayım" : "i am here")}
        </p>
        {writtenContinue && (
          <p
            className="mt-1 text-center text-[11px] tracking-[0.14em] text-slate-400"
            data-testid="written-continue-status"
          >
            {language === "tr" ? "Yazılı devam ediyorum." : "Continuing in writing."}
          </p>
        )}
        {castSimulated && (
          <p
            className="mt-1 rounded-full border border-amber-300/30 bg-amber-300/5 px-3 py-0.5 text-center text-[10px] uppercase tracking-[0.18em] text-amber-200/80"
            data-testid="cast-simulation-badge"
          >
            {language === "tr"
              ? "Simülasyon — gerçek ekran doğrulanmadı"
              : "Simulation — no physical display verified"}
          </p>
        )}
        {/* CTO Directive #121.4 — Physical Presence Score. Real
            device axes only. */}
        <PhysicalPresenceScore language={language} />
        {/* CTO Directive #122.1.3 — SSE-driven Living Orb hook.
            Renders ONLY when a voice-turn is in flight; every
            phase / label comes from verified server events. */}
        <WorkStreamOrb language={language} />
        {transcript && listening && (
          <p className="mt-3 max-w-xl text-center text-sm italic text-slate-400" data-testid="live-transcript">
            {`"${transcript}"`}
          </p>
        )}
        {goals.length > 0 && (
          <div
            className="mt-5 flex max-w-3xl flex-wrap items-center justify-center gap-2"
            data-testid="me-goals-tray"
          >
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
              {language === "tr" ? "Vizyonun" : "Your vision"}
            </span>
            {goals.map((g) => (
              <span
                key={g.id}
                data-testid={`me-goal-${g.id}`}
                title={g.kind}
                className="rounded-full border border-slate-700/70 bg-slate-900/40 px-3 py-1 text-[11px] text-slate-300 backdrop-blur-md"
              >
                {g.label}
              </span>
            ))}
          </div>
        )}
        {mind && (
          <div
            className="mt-4 flex max-w-2xl flex-col items-center gap-1 text-center"
            data-testid="me-mind-state"
          >
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
              {language === "tr" ? "Son değerlendirme" : "Latest assessment"}
            </span>
            <span className="text-[11px] italic text-slate-400">
              {language === "tr" ? mind.summary_tr : mind.summary_en}
            </span>
            <span className="mono text-[9px] tracking-wider text-slate-600">
              {language === "tr" ? "güven" : "confidence"} {Math.round((mind.confidence || 0) * 100)}%
              {" · "}
              {language === "tr" ? "belirsizlik" : "uncertainty"} {Math.round((mind.uncertainty || 0) * 100)}%
            </span>
          </div>
        )}
        {attention.length > 0 && (
          <div
            className="mt-4 flex max-w-2xl flex-col items-center gap-0.5 text-center"
            data-testid="me-attention-field"
          >
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
              {language === "tr" ? "Dikkat" : "Attention"}
            </span>
            <div className="flex flex-col items-center gap-0.5">
              {attention.map((p) => (
                <div
                  key={p.topic}
                  className="flex items-center gap-2"
                  data-testid={`me-attention-${p.topic.replace(/[^a-z0-9_-]/gi, "-")}`}
                >
                  <span className="mono text-[9px] text-slate-500">
                    {p.topic}
                  </span>
                  <div className="h-[3px] w-24 overflow-hidden rounded-full bg-slate-700/40">
                    <div
                      className={`h-full ${p.dissent ? "bg-amber-400/70" : "bg-sky-300/60"}`}
                      style={{ width: `${Math.min(100, Math.round((p.intensity || 0) * 100))}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {continuation.length > 0 && (
          <div
            className="mt-3 flex max-w-2xl flex-col items-center gap-1 text-center"
            data-testid="me-continuation"
          >
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
              {language === "tr" ? "Kaldığımız yer" : "Where we were"}
            </span>
            {continuation.map((c) => (
              <span
                key={c.id}
                className="text-[11px] italic text-slate-400/90"
                data-testid={`me-continuation-${c.id}`}
              >
                {language === "tr" ? (c.intention_tr || c.summary_tr) : (c.intention_en || c.summary_en)}
              </span>
            ))}
          </div>
        )}
        {unfinished.length > 0 && (
          <div
            className="mt-3 flex max-w-2xl flex-col items-center gap-1.5"
            data-testid="me-unfinished-tray"
          >
            <span className="mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
              {language === "tr" ? "Yarım kalan" : "Picking back up"}
            </span>
            {unfinished.map((run) => (
              <button
                key={run.id}
                type="button"
                onClick={() => resumeRun(run)}
                data-testid={`me-resume-${run.id}`}
                className="rounded-full border border-sky-300/40 bg-sky-300/10 px-4 py-1.5 text-xs text-sky-100/95 backdrop-blur-md transition-colors hover:border-sky-200/70 hover:text-sky-50"
              >
                {language === "tr"
                  ? `Devam edelim mi · ${run.subject}`
                  : `Continue · ${run.subject}`}
              </button>
            ))}
          </div>
        )}
        {actions.length > 0 && (
          <div
            className="mt-3 flex max-w-2xl flex-wrap items-center justify-center gap-2"
            data-testid="me-actions-tray"
          >
            {actions.map((a) => (
              <button
                key={a.kind}
                type="button"
                onClick={() => executeAction(a)}
                data-testid={`me-action-${a.kind}`}
                title={a.kind}
                className="rounded-full border border-amber-200/40 bg-amber-200/10 px-4 py-1.5 text-xs text-amber-100/90 backdrop-blur-md transition-colors hover:border-amber-200/70 hover:text-amber-50"
                style={{ animation: "me-invitation-emerge 600ms ease-out both" }}
              >
                {language === "tr" ? a.label_tr : a.label_en}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversation strip — appears only when there's content */}
      {messages.length > 0 && (
        <ConversationStrip
          messages={messages}
          language={language}
        />
      )}


      {error && (
        <div className="mt-6 max-w-xl rounded-md border border-rose-400/30 bg-rose-950/30 px-4 py-2 text-sm text-rose-200" data-testid="living-error">
          {error}
        </div>
      )}

      {/* Bottom intent bar */}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center px-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex w-full max-w-2xl items-center gap-2 rounded-2xl border border-slate-700/60 bg-slate-900/40 px-3 py-2 backdrop-blur-xl"
          data-testid="living-input-bar"
        >
          <button
            type="button"
            onClick={toggleVoice}
            disabled={!voiceSupported}
            data-testid="voice-toggle-btn"
            title={voiceSupported ? "Sesle konuş" : "Bu tarayıcı sesi desteklemiyor"}
            className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-colors ${
              listening
                ? "border-amber-300/60 bg-amber-300/10 text-amber-200"
                : "border-slate-700 text-slate-400 hover:text-white"
            } ${!voiceSupported ? "opacity-40" : ""}`}
          >
            {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.dcm,.xlsx,.xlsm,.docx,.txt,.csv,.md"
            onChange={onFileChosen}
            className="hidden"
            data-testid="living-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid="living-attach-btn"
            title="Belge yükle — tahlil, rapor, görüntü"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-700 text-slate-400 transition-colors hover:text-white"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={
              voiceSupported
                ? language === "tr"
                  ? "Konuş ya da yaz — ben buradayım."
                  : "Speak or type — I am here."
                : language === "tr"
                ? "Yaz, ben buradayım."
                : "Type, I am here."
            }
            data-testid="living-text-input"
            className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!textInput.trim()}
            data-testid="living-send-btn"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-200 text-slate-900 disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={() => setMessages([])}
              data-testid="living-clear-btn"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-700 text-slate-400 hover:text-white"
              title="Temizle"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </form>
      </div>
    </div>
  );
}



/**
 * CTO Directive #126.2 §P0-D — Smart-scrolling Living Conversation strip.
 *
 * • Renders the last 6 turns (workspace / user / ME) in a scroll
 *   container that auto-follows the tail when the user is near the
 *   bottom.
 * • If the user has scrolled up to read history, we do NOT force
 *   them back down; a discreet pill "Yeni yanıt var — en alta dön"
 *   appears until they either return manually or send a new turn.
 * • SSE token growth in an active streaming reply extends the
 *   height gently — no bouncing.
 */
function ConversationStrip({ messages, language }) {
  const activeSuffix = React.useMemo(() => {
    const last = messages[messages.length - 1];
    if (!last) return "";
    return last.streaming ? String(last.content || "") : "";
  }, [messages]);

  const {
    scrollRef, bottomRef, newBelow, scrollToBottom,
  } = useSmartAutoScroll({
    deps: [messages.length, activeSuffix],
    nearBottomPx: 120,
  });

  const visible = messages.slice(-6);

  return (
    <div className="relative mt-8 w-full max-w-2xl">
      <div
        ref={scrollRef}
        data-testid="conversation-strip"
        className="max-h-[42vh] space-y-3 overflow-y-auto pr-1 text-sm"
        style={{ scrollBehavior: "smooth" }}
      >
        {visible.map((m, i) => {
          if (m.role === "workspace") {
            const ws = m.workspace || {};
            return (
              <div
                key={i}
                data-testid={`msg-workspace-${i}`}
                className="rounded-lg border border-amber-200/20 bg-amber-50/[0.04] px-4 py-3 backdrop-blur-md"
              >
                <div className="mono mb-2 text-[10px] uppercase tracking-[0.18em] text-amber-300/80">
                  {language === "tr" ? "Hazırladıklarım" : "What I gathered"}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
                  {ws.intent && (
                    <span className="mios-pill mios-pill-normal mono">
                      {ws.intent.intent} · {Math.round((ws.intent.confidence || 0) * 100)}%
                    </span>
                  )}
                  {ws.warm_count > 0 && (
                    <span className="rounded-md border border-slate-700/60 px-2 py-0.5">
                      {language === "tr" ? "Önbellek" : "Warm"} · {ws.warm_count}
                    </span>
                  )}
                  {ws.evidence_count > 0 && (
                    <span className="rounded-md border border-slate-700/60 px-2 py-0.5">
                      {language === "tr" ? "Kanıt" : "Evidence"} · {ws.evidence_count}
                    </span>
                  )}
                </div>
                {(ws.actions || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {ws.actions.map((act) => (
                      <Link
                        key={act.id}
                        to={act.kind ? `/production?kind=${act.kind}` : "/dashboard"}
                        data-testid={`workspace-action-${act.id}`}
                        className="rounded-md border border-amber-200/30 bg-amber-200/10 px-2.5 py-1 text-xs text-amber-100 transition-colors hover:bg-amber-200/20"
                      >
                        {language === "tr" ? act.label_tr : act.label_en}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div
              key={i}
              data-testid={`msg-${m.role}-${i}`}
              className={`rounded-lg border px-4 py-3 backdrop-blur-md ${
                m.role === "user"
                  ? "border-slate-700/50 bg-slate-900/40 text-slate-200"
                  : "border-amber-200/15 bg-amber-50/5 text-slate-100"
              }`}
            >
              <div className="mono mb-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                {m.role === "user" ? (language === "tr" ? "Sen" : "You") : "ME"}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed">
                {m.content || (m.streaming ? "…" : "")}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} data-testid="conversation-bottom-anchor" />
      </div>
      {newBelow && (
        <button
          type="button"
          onClick={() => scrollToBottom("smooth")}
          data-testid="new-response-pill"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-amber-200/40 bg-slate-900/80 px-3 py-1 text-xs text-amber-100 shadow-lg backdrop-blur-md transition-opacity hover:bg-slate-800/90"
        >
          {language === "tr" ? "Yeni yanıt var — en alta dön" : "New reply — jump to bottom"}
        </button>
      )}
    </div>
  );
}
